import { isAfter, parseISO, startOfDay } from 'date-fns'
import { PhoneNumberUtil } from 'google-libphonenumber'
import { z } from 'zod'

import { spaToday } from '../utils/timeSlots'

/** Laravel exposes neither the guest cap nor the hours list, so hardcoding is the end state. */
export const GUEST_OPTIONS = [1, 2] as const
export const HOURS_OPTIONS = [
  '1:00',
  '1:30',
  '2:00',
  '2:30',
  '3:00',
  '3:30',
  '4:00',
  '4:30',
  '5:00'
] as const

/** Booking JWT TTL — Laravel mints it for 60 minutes. */
export const BOOKING_TOKEN_TTL_MS = 60 * 60 * 1000

/** Same approach as the payment forms: libphonenumber validity plus obvious-junk patterns. */
export function isValidPhone(e164: string): boolean {
  if (!/^\+[1-9]\d{6,15}$/.test(e164)) return false

  try {
    const util = PhoneNumberUtil.getInstance()
    const parsed = util.parse(e164, undefined)
    if (!util.isValidNumber(parsed) || !util.isPossibleNumber(parsed)) return false
  } catch {
    return false
  }

  const digits = e164.replace(/\D/g, '')
  if (/^0+$/.test(digits)) return false
  if (/^(.)\1{6,}$/.test(digits)) return false
  if (/^1234567/.test(digits) || /^7654321/.test(digits)) return false

  return true
}

/** Verify form — matched by Laravel against `g_gift.receive_from` (LIKE) and `gift_number` (exact). */
export const verifySchema = z.object({
  giftFrom: z.string().trim().min(1, 'REQUIRED').max(200, 'TOO_LONG'),
  giftNumber: z.string().trim().min(1, 'REQUIRED').max(50, 'TOO_LONG')
})

export type VerifyFormValues = z.infer<typeof verifySchema>

/** Deeplink payload — every `?token=` ever minted carries exactly these two fields. */
export const redeemDeeplinkSchema = verifySchema

/**
 * Booking form. A factory because `giftExpires` is runtime data.
 * `date` is bounded by today and expiry, calendar-day only — the same-day time cut lives in the
 * slot list, which depends on the branch's hours.
 */
export function makeBookingSchema(giftExpires: string) {
  const expiry = startOfDay(parseISO(giftExpires))

  return z.object({
    branch: z.coerce.number().int().positive('REQUIRED'),
    date: z
      .string()
      .min(1, 'REQUIRED')
      .refine((v) => !Number.isNaN(parseISO(v).valueOf()), 'INVALID_DATE')
      .refine((v) => v.slice(0, 10) >= spaToday(), 'DATE_PAST')
      .refine((v) => !isAfter(startOfDay(parseISO(v)), expiry), 'DATE_AFTER_EXPIRY'),
    time: z.string().regex(/^\d{2}:\d{2}$/, 'REQUIRED'),
    hours: z.enum(HOURS_OPTIONS),
    guest: z.coerce.number().refine((v): v is 1 | 2 => v === 1 || v === 2, 'REQUIRED'),
    yourName: z.string().trim().min(1, 'REQUIRED').max(150, 'TOO_LONG'),
    yourEmail: z.string().trim().min(1, 'EMAIL_REQUIRED').email('EMAIL_FORMAT'),
    yourPhone: z.string().trim().min(1, 'REQUIRED').refine(isValidPhone, 'PHONE_FORMAT'),
    remark: z.string().trim().max(1000, 'TOO_LONG').optional().default('')
  })
}

export type BookingFormValues = z.infer<ReturnType<typeof makeBookingSchema>>

/** The five fields that decide whether a room is free. No customer details: unneeded, unsent. */
export type AvailabilityInput = Pick<
  BookingFormValues,
  'branch' | 'date' | 'time' | 'hours' | 'guest'
>

/** Caps length and rejects non-base64 chars before decoding, so a hostile query never reaches atob. */
export function decodeRedeemToken(raw?: string | null) {
  if (!raw || raw.length > 2048) return null

  const token = raw.replace(/ /g, '+')

  if (!/^[A-Za-z0-9+/=]*$/.test(token)) return null

  try {
    const query = Buffer.from(token, 'base64').toString('utf8')
    const params = new URLSearchParams(query)
    const parsed = redeemDeeplinkSchema.safeParse({
      giftFrom: params.get('giftFrom') ?? '',
      giftNumber: params.get('giftNumber') ?? ''
    })

    return parsed.success ? parsed.data : null
  } catch {
    // Malformed base64. Drop the prefill silently — an old link must not surface an error.
    return null
  }
}
